import { CanonicalAnalysisOutput, SummaryCard, SummaryQuote } from '@digital-twins/shared-types'

/**
 * The two executive-summary fields the Summary tab drew before analysis wrote
 * `summaryCards` (9c78f4b2e). Studies analysed before then carry these and no
 * cards, so without this read their Featured and Quotes frames are empty.
 *
 * Read by the report page, not the tab: the tab renders the cards it is given
 * and never reads the executive summary.
 */
export interface LegacySummaryInput {
  featuredCard?: { questionId?: string | null } | null
  featuredQuotes?: Array<{
    text?: string
    fullText?: string
    displayText?: string
    attribution?: string
    name?: string
    role?: string
    questionLabel?: string
    sentiment?: string
  }> | null
}

const QUOTE_SENTIMENTS: Array<NonNullable<SummaryQuote['sentiment']>> = [
  'positive',
  'neutral',
  'negative',
  'mixed',
]

/**
 * @legacy Read adapter for analyses stored before `summaryCards` (plots plan S): maps the
 * stored `executiveSummary.featuredQuotes` onto the stored `SummaryQuote` shape, with no
 * `plot`, so the quotes card draws it through the quote-list legacy reader
 * (`quotesCardLegacyAdapter`). Never used for a newly built analysis; listed in the V keep
 * list and deleted with the last stored analysis of this shape.
 */
function toSummaryQuote(
  quote: NonNullable<LegacySummaryInput['featuredQuotes']>[number],
): SummaryQuote | null {
  const text = (quote.displayText || quote.text || quote.fullText || '').trim()
  if (!text) {
    return null
  }
  const sentiment = QUOTE_SENTIMENTS.find((value) => value === quote.sentiment) ?? null
  return {
    text,
    speakerName: quote.name || quote.attribution || null,
    role: quote.role || null,
    sentiment,
    questionText: quote.questionLabel || null,
  }
}

/**
 * Cards for a study whose analysis predates `summaryCards`, built from what its
 * stored executive summary already featured: the same question, drawn by the
 * Survey Data tab's card (the `fallback` shape), and the same quotes.
 *
 * Read-time only and never persisted. A study that has `summaryCards` never
 * reaches this, and a field the old summary lacks leaves its slot empty.
 */
export function buildLegacySummaryCards(legacy: LegacySummaryInput | null | undefined): Array<SummaryCard> {
  if (!legacy) {
    return []
  }
  const cards: Array<SummaryCard> = []

  const questionId = legacy.featuredCard?.questionId?.trim()
  if (questionId) {
    cards.push({
      slot: 'plot',
      shape: 'fallback',
      status: 'available',
      payload: { shape: 'fallback', questionId },
    })
  }

  const quotes = (legacy.featuredQuotes ?? [])
    .map(toSummaryQuote)
    .filter((quote): quote is SummaryQuote => quote !== null)
  if (quotes.length > 0) {
    cards.push({
      slot: 'quotes',
      shape: 'quotes',
      status: 'available',
      payload: { shape: 'quotes', quotes },
    })
  }

  return cards
}

/**
 * The analysis the Summary tab draws: unchanged when it has `summaryCards`,
 * otherwise with the cards its stored executive summary implies.
 */
export function withLegacySummaryCards(
  analysis: CanonicalAnalysisOutput | null | undefined,
  legacy: LegacySummaryInput | null | undefined,
): CanonicalAnalysisOutput | null | undefined {
  if (!analysis || analysis.summaryCards) {
    return analysis
  }
  const summaryCards = buildLegacySummaryCards(legacy)
  if (summaryCards.length === 0) {
    return analysis
  }
  return { ...analysis, summaryCards }
}
